/**
 * Pure helpers for the task board, list and detail (grouping, drag & drop
 * placement, state machine affordances, realtime cache patches, form values).
 */
import type { TaskEventView } from "@/api/generated/TaskEventView"
import type { TaskSummary } from "@/api/generated/TaskSummary"
import { TASK_STATUSES, type TaskFilters, type TaskOrder, type TaskStatus } from "@/api/queries/tasks"

/** Spacing the server uses for manual lane ordering (`SORT_STEP`). */
export const SORT_STEP = 1024

/**
 * Manual status changes the UI offers (mirrors `transition_allowed` in
 * `mit-tasks/src/model.rs`). `done → progress` exists server-side but only as
 * the follow-up message flow, so it is not offered as a plain status change.
 */
export const ALLOWED_TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  backlog: ["todo", "blocked", "done"],
  todo: ["backlog", "progress", "blocked", "done"],
  progress: ["review", "blocked", "done"],
  review: ["progress", "blocked", "done"],
  blocked: ["backlog", "todo", "progress", "review", "done"],
  done: [],
}

export function isTaskStatus(v: unknown): v is TaskStatus {
  return typeof v === "string" && (TASK_STATUSES as readonly string[]).includes(v)
}

export function canTransition(from: string, to: string): boolean {
  if (!isTaskStatus(from) || !isTaskStatus(to)) return false
  return ALLOWED_TRANSITIONS[from].includes(to)
}

/** Can a card dragged from `from` be dropped in lane `to`? Same-lane reorders always can. */
export function canDropTo(from: string, to: string): boolean {
  return from === to || canTransition(from, to)
}

export const byLaneOrder = (a: TaskSummary, b: TaskSummary) => a.sort_order - b.sort_order || a.id - b.id

/** Tasks grouped into board lanes, each sorted by manual order. */
export function groupByStatus(tasks: readonly TaskSummary[]): Record<TaskStatus, TaskSummary[]> {
  const groups = Object.fromEntries(TASK_STATUSES.map((s) => [s, [] as TaskSummary[]])) as Record<
    TaskStatus,
    TaskSummary[]
  >
  for (const t of tasks) if (isTaskStatus(t.status)) groups[t.status].push(t)
  for (const s of TASK_STATUSES) groups[s].sort(byLaneOrder)
  return groups
}

export interface Placement {
  after_id: number | null
  before_id: number | null
  /** Optimistic `sort_order` until the server answers. */
  sort_order: number
}

/**
 * Where a card lands when dropped into `lane` at `index` (index among the
 * lane's other cards): the neighbours for `POST /tasks/{id}/reorder` and an
 * optimistic sort order between them.
 */
export function placementInLane(lane: readonly TaskSummary[], index: number, draggedId: number): Placement {
  const others = lane.filter((t) => t.id !== draggedId)
  const i = Math.max(0, Math.min(index, others.length))
  const prev = i > 0 ? others[i - 1] : undefined
  const next = i < others.length ? others[i] : undefined
  let sort_order: number
  if (prev && next) sort_order = (prev.sort_order + next.sort_order) / 2
  else if (prev) sort_order = prev.sort_order + SORT_STEP
  else if (next) sort_order = next.sort_order - SORT_STEP
  else sort_order = SORT_STEP
  return { after_id: prev?.id ?? null, before_id: prev ? null : (next?.id ?? null), sort_order }
}

/** Optimistically moves a task to `status` with `sort_order` in a list. */
export function moveTask(
  items: readonly TaskSummary[],
  id: number,
  status: TaskStatus,
  sort_order: number,
): TaskSummary[] {
  return items.map((t) =>
    t.id === id
      ? { ...t, status, display_status: t.status === status ? t.display_status : status, sort_order }
      : t,
  )
}

/**
 * Does a task row (e.g. from a realtime `task.upserted`) belong in a list
 * loaded with `f`? The text search is not checked here: the server also
 * searches descriptions, which a summary does not carry.
 */
export function matchesFilters(t: TaskSummary, f: TaskFilters): boolean {
  if (f.status) {
    if (f.status === "failed" || f.status === "cancelled") {
      if (t.status !== "done" || t.closure_kind !== f.status) return false
    } else if (f.status === "waiting") {
      if (t.status !== "progress" || !t.has_pending_human_request) return false
    } else if (t.status !== f.status) return false
  }
  if (f.project_id != null && t.project_id !== f.project_id) return false
  if (f.standalone && t.project_id != null) return false
  if (f.assignee_kind && t.assignee_kind !== f.assignee_kind) return false
  if (f.assignee_id != null && t.assignee_id !== f.assignee_id) return false
  if (f.priority && t.priority !== f.priority) return false
  if (f.label && !t.labels.includes(f.label)) return false
  return true
}

export type ListPatch = { kind: "replace"; items: TaskSummary[] } | { kind: "stale" } | { kind: "none" }

/**
 * How a cached list reacts to a realtime task row: rows already in the list
 * are replaced in place; rows that left or entered the filter mark the list
 * stale (it is refetched so totals and pagination stay right).
 */
export function patchForUpsert(items: readonly TaskSummary[], row: TaskSummary, f: TaskFilters): ListPatch {
  const idx = items.findIndex((t) => t.id === row.id)
  const matches = matchesFilters(row, f)
  if (idx === -1) return matches ? { kind: "stale" } : { kind: "none" }
  if (!matches) return { kind: "stale" }
  const next = items.slice()
  next[idx] = row
  return { kind: "replace", items: next }
}

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 }

export const priorityRank = (p: string) => PRIORITY_RANK[p] ?? 2

/** Client-side mirror of the server's `order_by` (used after in-place patches). */
export function sortTasks(items: readonly TaskSummary[], order: TaskOrder = "sort_order"): TaskSummary[] {
  const out = items.slice()
  switch (order) {
    case "created_at":
      return out.sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
    case "updated_at":
      return out.sort((a, b) => b.updated_at.localeCompare(a.updated_at) || b.id - a.id)
    case "due_at":
      return out.sort((a, b) => {
        if (a.due_at === b.due_at) return a.id - b.id
        if (!a.due_at) return 1
        if (!b.due_at) return -1
        return a.due_at.localeCompare(b.due_at)
      })
    case "priority":
      return out.sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority) || byLaneOrder(a, b))
    default:
      return out.sort(
        (a, b) =>
          a.status.localeCompare(b.status) ||
          a.sort_order - b.sort_order ||
          b.created_at.localeCompare(a.created_at),
      )
  }
}

// ------------------------------------------------------------------------------ assignees

export type AssigneeRef = { assignee_kind: "agent" | "team"; assignee_id: number } | { assignee_kind: "pool" }

/** `agent:3` / `team:2` / `pool` → assign body. */
export function parseAssigneeKey(key: string | null | undefined): AssigneeRef | null {
  if (!key) return null
  if (key === "pool") return { assignee_kind: "pool" }
  const [kind, raw] = key.split(":")
  const id = Number(raw)
  if ((kind === "agent" || kind === "team") && Number.isInteger(id) && id > 0) {
    return { assignee_kind: kind, assignee_id: id }
  }
  return null
}

export function assigneeKey(kind: string | null | undefined, id: number | null | undefined): string | null {
  if (kind === "pool") return "pool"
  if ((kind === "agent" || kind === "team") && id != null) return `${kind}:${id}`
  return null
}

// ------------------------------------------------------------------------------ dates

/** ISO timestamp → value for `<input type="datetime-local">` (local time). */
export function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromDatetimeLocal(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Due date in the past on an open task. */
export function isOverdue(t: Pick<TaskSummary, "due_at" | "status">, now = Date.now()): boolean {
  if (!t.due_at || t.status === "done") return false
  const d = new Date(t.due_at).getTime()
  return Number.isFinite(d) && d < now
}

// ------------------------------------------------------------------------------ inputs & checklist

export interface InputParam {
  name: string
  required: boolean
  label?: string
  description?: string
}

/** Normalises `input_spec` (a list of `{name, required}`; an object keyed by name is tolerated). */
export function inputParams(spec: unknown): InputParam[] {
  const str = (v: unknown) => (typeof v === "string" ? v : undefined)
  if (Array.isArray(spec)) {
    return spec
      .filter((p): p is Record<string, unknown> => !!p && typeof p === "object" && typeof p.name === "string")
      .map((p) => ({
        name: p.name as string,
        required: p.required === true,
        label: str(p.label),
        description: str(p.description),
      }))
  }
  if (spec && typeof spec === "object") {
    return Object.entries(spec as Record<string, unknown>).map(([name, v]) => {
      const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {}
      return { name, required: o.required === true, label: str(o.label), description: str(o.description) }
    })
  }
  return []
}

/** Text value for an input field from stored `input_values`. */
export function inputValueText(v: unknown): string {
  if (v == null) return ""
  return typeof v === "string" ? v : JSON.stringify(v)
}

export type ChecklistItem = TaskSummary["checklist"][number]

/**
 * Id for a checklist item added in the UI (the server keeps non-empty ids). Items added
 * before the previous save returned must not share one, or toggling/removing one hits both.
 * `getRandomValues` also works outside secure contexts, unlike `randomUUID`.
 */
export function newChecklistId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
}

export function checklistProgress(items: readonly ChecklistItem[]): { done: number; total: number } {
  return { done: items.filter((i) => i.done).length, total: items.length }
}

/**
 * Why the task's last run failed: the `error` the server recorded on the
 * latest close as failed (`status_changed` → done/failed), if any.
 */
export function failureReason(
  events: readonly Pick<TaskEventView, "kind" | "payload">[],
): string | undefined {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!
    if (e.kind !== "status_changed" || e.payload.to !== "done") continue
    if (e.payload.closure_kind !== "failed") return undefined
    return typeof e.payload.error === "string" && e.payload.error.trim() ? e.payload.error.trim() : undefined
  }
  return undefined
}

/** Tasks needing a human: an open question/approval, or a result waiting for review. */
export const needsAttention = (t: Pick<TaskSummary, "status" | "has_pending_human_request">) =>
  t.has_pending_human_request || t.status === "review"
